import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import TopMenu from "../../components/TopMenu/TopMenu";
import PhoneInput from "../../components/PhoneInput/PhoneInput";
import { updateMyContactInfo } from "../../api/UsersApi";
import { useAuth } from "../../util/Auth";
import { isValidE164Phone, isValidEmail } from "../../util/contactInfoPolicy";
import "./UpdateContactInfo.css";

type FormErrors = {
  phone?: string;
  preferredEmail?: string;
};

export default function UpdateContactInfo() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, setUser } = useAuth();

  const [phone, setPhone] = useState("");
  const [preferredEmail, setPreferredEmail] = useState(user?.preferred_email ?? "");
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const trimmedPhone = phone.trim();
    const trimmedPreferredEmail = preferredEmail.trim();

    const nextErrors: FormErrors = {};

    if (!trimmedPhone || !isValidE164Phone(trimmedPhone)) {
      nextErrors.phone = t("pages.updateContactInfo.phoneError");
    }

    if (!trimmedPreferredEmail || !isValidEmail(trimmedPreferredEmail)) {
      nextErrors.preferredEmail = t("pages.updateContactInfo.preferredEmailError");
    }

    setErrors(nextErrors);
    setSubmitError(null);

    if (Object.keys(nextErrors).length > 0) {
      return;
    }

    setIsSubmitting(true);

    try {
      await updateMyContactInfo({
        phone: trimmedPhone,
        preferred_email: trimmedPreferredEmail
      });

      setUser({
        ...user,
        phone: trimmedPhone,
        preferred_email: trimmedPreferredEmail,
        contact_info_updated_date_utc: new Date().toISOString()
      });

      navigate("/user-home");
    } catch {
      setSubmitError(t("pages.updateContactInfo.submitError"));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="update-contact-info-page">
      <TopMenu />

      <main className="update-contact-info-content">
        <section className="update-contact-info-hero" aria-labelledby="update-contact-info-title">
          <h1 id="update-contact-info-title" className="update-contact-info-title">
            {t("pages.updateContactInfo.title")}
          </h1>
          <p className="update-contact-info-subtitle">{t("pages.updateContactInfo.subtitle")}</p>

          <div className="update-contact-info-current-values" aria-live="polite">
            <div className="update-contact-info-current-item">
              <span className="update-contact-info-current-label">{t("pages.updateContactInfo.currentEmail")}</span>
              <span className="update-contact-info-current-value">{user?.preferred_email ?? user?.email ?? "—"}</span>
            </div>

            <div className="update-contact-info-current-item">
              <span className="update-contact-info-current-label">{t("pages.updateContactInfo.currentPhone")}</span>
              <span className="update-contact-info-current-value">{user?.phone ?? t("pages.updateContactInfo.noPhoneSet")}</span>
            </div>
          </div>

          <form className="update-contact-info-form" onSubmit={handleSubmit} noValidate>
            <div className="update-contact-info-field">
              <PhoneInput
                value={phone}
                onChange={setPhone}
                label={t("pages.updateContactInfo.phone")}
                placeholder={t("pages.updateContactInfo.phonePlaceholder")}
                className={errors.phone ? "has-error" : ""}
              />
              {errors.phone && (
                <span id="phone-error" className="update-contact-info-error" role="alert">
                  {errors.phone}
                </span>
              )}
            </div>

            <label className="update-contact-info-field" htmlFor="preferred-email">
              <span className="update-contact-info-label">{t("pages.updateContactInfo.preferredEmail")}</span>
              <input
                id="preferred-email"
                name="preferred_email"
                type="email"
                autoComplete="email"
                className={`update-contact-info-input ${errors.preferredEmail ? "has-error" : ""}`}
                placeholder={t("pages.updateContactInfo.preferredEmailPlaceholder")}
                value={preferredEmail}
                onChange={(event) => setPreferredEmail(event.target.value)}
                disabled={isSubmitting}
                aria-invalid={Boolean(errors.preferredEmail)}
                aria-describedby={errors.preferredEmail ? "preferred-email-error" : undefined}
              />
              {errors.preferredEmail && (
                <span id="preferred-email-error" className="update-contact-info-error" role="alert">
                  {errors.preferredEmail}
                </span>
              )}
            </label>

            {submitError && (
              <p className="update-contact-info-submit-error" role="alert">
                {submitError}
              </p>
            )}

            <button type="submit" className="inscrever-btn update-contact-info-submit" disabled={isSubmitting}>
              {isSubmitting ? t("pages.updateContactInfo.submitting") : t("pages.updateContactInfo.submit")}
            </button>
          </form>
        </section>
      </main>
    </div>
  );
}
